// Generated by GitHub Copilot
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'
import { PermissionGuard } from '#components/tools/permission-guard.tsx'
import { PermissionAction, PermissionService } from '#core/entities/auth/roles/Permission.ts'
import { useUserState } from '#core/state/UserState.ts'
import BugReportsApi from '#core/api/operations/BugReports.ts'
import BugReport from '#core/entities/operations/bug-reports/BugReport.ts'
import { BugReportModal } from '#components/domain/operations/bug-reports/bug-report-modal.tsx'

const api = new BugReportsApi()

export const Route = createFileRoute('/_appSettings/$companyId/settings/bug-reports')({
  component: RouteComponent,
})

/**
 * Normal-user bug-reports view in Settings: file a report and — when the user has
 * BUG_REPORTS.READ — see their own submissions read-only. The full cross-report
 * triage (status changes, comments, every user's reports) lives in the gated
 * `/super-user/bug-reports` section. This is a non-super-user surface, so it must
 * NOT import anything from a `super-user/` path (the ESLint boundary enforces that).
 */
function RouteComponent() {
  const { t } = useTranslation('bug-reports')
  const userState = useUserState()
  const canRead = userState.hasPermission(PermissionService.BUG_REPORTS, PermissionAction.READ)
  const [reports, setReports] = useState<BugReport[]>([])
  const [loading, setLoading] = useState(false)
  const [isCreating, setIsCreating] = useState(false)

  const load = useCallback(() => {
    if (!canRead) {
      return
    }
    setLoading(true)
    // Non-super users receive only their OWN reports from this endpoint (server-scoped).
    api
      .listBugReports({ take: 100 })
      .then((result) => setReports(result.results))
      .catch(() => { /* non-critical: show empty */ })
      .finally(() => setLoading(false))
  }, [canRead])

  useEffect(() => { load() }, [load])

  return (
    <div className="container py-4" style={{ maxWidth: '52rem' }}>
      <div className="ynt-page-header-row-top">
        <div>
          <h4 className="mb-1">{t('settings.title')}</h4>
          <p className="text-muted mb-0">{t('settings.intro')}</p>
        </div>
        <PermissionGuard permissions={[[[PermissionService.BUG_REPORTS, PermissionAction.CREATE]]]}>
          <button type="button" className="btn btn-primary d-inline-flex align-items-center gap-2" onClick={() => setIsCreating(true)}>
            <Plus size={16} /> {t('page.newReport')}
          </button>
        </PermissionGuard>
      </div>

      {canRead ? (
        <div className="mt-4">
          <h6 className="text-uppercase text-secondary mb-2">{t('settings.myReportsTitle')}</h6>
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>{t('shared.fields.title')}</th>
                  <th>{t('shared.fields.type')}</th>
                  <th>{t('shared.fields.status')}</th>
                  <th>{t('columns.created')}</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={4} className="text-center text-muted py-4">{t('shared.common.loading')}</td></tr>}
                {!loading && reports.length === 0 && (
                  <tr><td colSpan={4} className="text-center text-muted py-4">{t('settings.myReportsEmpty')}</td></tr>
                )}
                {!loading && reports.map((report) => (
                  <tr key={report.id}>
                    <td>{report.title || t('untitled')}</td>
                    <td>{t(`kind.${report.kind}`)}</td>
                    <td><span className="badge text-bg-light border">{t(`status.${report.status}`)}</span></td>
                    <td>{BugReport.formatDate(report.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="alert alert-light border mt-4 mb-0">{t('settings.noReadNote')}</div>
      )}

      <BugReportModal isOpen={isCreating} onClose={() => setIsCreating(false)} onSubmitted={load} />
    </div>
  )
}
